// Auth client — wraps fetch para anexar Bearer + Argon2id browser-side.
//
// O painel de recurso compartilha o endpoint /api/comite/login com o comitê
// de 1ª instância — o servidor mintará um JWT cujas `roles` incluem
// `comite_recurso` quando a conta for de painel de 2ª instância. RBAC
// distinto (D-69) é aplicado nas rotas /api/recurso/* via requireRole.
//
// Senha do membro vai client-side via Argon2id (D-98 — paridade com intake).
// Salt = sha256(email_normalizado). Servidor recebe authHash[0..32] em base64;
// kekRaw[32..64] permanece no browser (NÃO é usado em login mas pode ser
// reutilizado para unwrap de DEK de caso atribuído quando KEK_membro entrar
// em Phase 6 — para Phase 5-MVP usamos KEK_comite global no servidor).

import { deriveKeyMaterial, splitKeyMaterial } from '@aions/crypto';
import { bytesToBase64 } from './bytes';
import { apiUrl } from './api-base';

const STORAGE_KEY = 'aions:recurso:session:v1';

/** Role obrigatória para acessar o painel de recurso. */
export const REQUIRED_ROLE = 'comite_recurso';

export interface SessionState {
  token: string;
  user: {
    id: string;
    email: string;
    roles: string[];
    /** Sprint 3.1 — true se aceitou versão atual LGP-02. False abre PolicyAcceptanceModal. */
    policyAccepted?: boolean;
    policyVersion?: string;
  };
  expInactivity?: number;
}

let memSession: SessionState | null = null;

export function getStoredSession(): SessionState | null {
  if (memSession) return memSession;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionState;
    memSession = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(s: SessionState | null): void {
  memSession = s;
  if (s) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // sessionStorage indisponível — degradar para memória apenas.
    }
  } else {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Hashes the password client-side using the same Argon2id pipeline as intake. */
async function deriveAuthHash(email: string, password: string): Promise<string> {
  const material = await deriveKeyMaterial(password, email.trim().toLowerCase());
  const { authHash } = splitKeyMaterial(material);
  return bytesToBase64(authHash);
}

export interface LoginResult {
  ok: boolean;
  /**
   * Códigos:
   *   - 'invalid_credentials' (401)
   *   - 'account_locked' (423)
   *   - 'missing_role' (login OK mas sem role comite_recurso)
   *   - 'network' (fetch ou Argon2id falhou)
   */
  error?: string;
  session?: SessionState;
}

export async function loginWithPassword(
  email: string,
  password: string,
  totpCode: string,
): Promise<LoginResult> {
  let authHashB64: string;
  try {
    authHashB64 = await deriveAuthHash(email, password);
  } catch (err) {
    void err;
    return { ok: false, error: 'network' };
  }

  let resp: Response;
  try {
    resp = await fetch(apiUrl('/api/comite/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), authHash: authHashB64, totpCode }),
    });
  } catch {
    return { ok: false, error: 'network' };
  }

  if (resp.status === 423) return { ok: false, error: 'account_locked' };
  if (!resp.ok) return { ok: false, error: 'invalid_credentials' };

  const data = (await resp.json()) as {
    token: string;
    user: {
      id: string;
      email: string;
      roles: string[];
      policyAccepted?: boolean;
      policyVersion?: string;
    };
  };

  // Phase 5 — bloquear quem não tem role comite_recurso DESDE o login client-side
  // (defensa em profundidade — server-side já bloqueia em /api/recurso/* via
  // requireRole). Isso evita que um comite_membro logue no painel errado e veja
  // mensagens vazias.
  if (!data.user.roles.includes(REQUIRED_ROLE)) {
    return { ok: false, error: 'missing_role' };
  }

  const session: SessionState = { token: data.token, user: data.user };
  setSession(session);
  return { ok: true, session };
}

export async function logout(): Promise<void> {
  const s = getStoredSession();
  if (s) {
    try {
      await fetch(apiUrl('/api/comite/login/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${s.token}` },
      });
    } catch {
      // silently ignore — local-side cleanup ainda acontece
    }
  }
  setSession(null);
}

/** Sprint 3.1 — POST /policy-accept (mesmo endpoint compartilhado). */
export async function acceptPolicy(): Promise<{ ok: boolean; error?: string }> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  try {
    const resp = await fetch(apiUrl('/api/comite/policy-accept'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${s.token}` },
    });
    if (!resp.ok) return { ok: false, error: `http_${String(resp.status)}` };
    const data = (await resp.json()) as { policyVersion: string };
    setSession({
      ...s,
      user: { ...s.user, policyAccepted: true, policyVersion: data.policyVersion },
    });
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

/** fetch wrapper that injects Bearer + auto-refreshes session on X-Session-Refresh header. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const s = getStoredSession();
  const headers = new Headers(init.headers);
  if (s) headers.set('Authorization', `Bearer ${s.token}`);
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(apiUrl(path), { ...init, headers });
  // Sliding refresh — server emits header on every authenticated request
  const refreshed = resp.headers.get('X-Session-Refresh');
  if (refreshed && s) {
    setSession({ ...s, token: refreshed });
  }
  if (resp.status === 401) {
    setSession(null);
  }
  return resp;
}

export interface ChangePasswordResult {
  ok: boolean;
  error?: string;
}

/**
 * F3-D — troca de senha autenticada (mesmo contrato do web-comite). Deriva o
 * authHash ATUAL e o NOVO (Argon2id client-side, mesmo pipeline do login) e
 * POSTa para /api/comite/change-password (endpoint requireAuth-only: qualquer
 * papel troca a PRÓPRIA senha). Senha NUNCA trafega em claro.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  totpCode: string,
): Promise<ChangePasswordResult> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  let currentAuthHash: string;
  let newAuthHash: string;
  try {
    currentAuthHash = await deriveAuthHash(s.user.email, currentPassword);
    newAuthHash = await deriveAuthHash(s.user.email, newPassword);
  } catch {
    return { ok: false, error: 'network' };
  }
  try {
    const resp = await apiFetch('/api/comite/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentAuthHash, newAuthHash, totpCode }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'submit_failed' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export interface FirstAccessResult {
  ok: boolean;
  error?: string;
}

/**
 * F3-C — ativação de primeiro acesso. Deriva o authHash (Argon2id client-side) e
 * POSTa /api/comite/first-access com Bearer=firstAccessToken (JWT one-shot emitido
 * pelo admin; o endpoint é role-agnóstico). PRÉ-AUTH: usa fetch cru (não apiFetch,
 * que injeta sessão e limpa em 401). Senha NUNCA trafega em claro.
 */
export async function activateFirstAccess(
  token: string,
  email: string,
  password: string,
  totpCode: string,
): Promise<FirstAccessResult> {
  try {
    const authHash = await deriveAuthHash(email, password);
    const resp = await fetch(apiUrl('/api/comite/first-access'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authHash, totpCode }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'generic' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}
